import {gqlFetch, throwUserErrors} from './product-configurator';
import {isMetaobjectHandle} from '../metaobject-handles.js';
import {DISCLAIMER_TYPE, disclaimerFromMetaobject, plainTextToRichText} from '../disclaimers.js';

export const disclaimerFields = 'id type handle fields { key value }';

// Merchant-owned definitions keep disclaimers editable by merchants and other apps.
export async function ensureDisclaimerDefinitions() {
  const result = await gqlFetch(`#graphql
    query DisclaimerDefinitions {
      metaobjectDefinitionByType(type: "evp_configurator_disclaimer") {
        id access { storefront } fieldDefinitions { key name type { name } }
      }
    }
  `);
  let definition = result.data.metaobjectDefinitionByType;
  if (!definition) {
    const created = await gqlFetch(`#graphql
      mutation CreateDisclaimerDefinition($definition: MetaobjectDefinitionCreateInput!) {
        metaobjectDefinitionCreate(definition: $definition) {
          metaobjectDefinition { id access { storefront } fieldDefinitions { key name type { name } } }
          userErrors { field message code }
        }
      }
    `, {definition: {type: DISCLAIMER_TYPE, name: 'Configurator disclaimer', displayNameKey: 'name', access: {storefront: 'PUBLIC_READ'}, fieldDefinitions: [
      {key: 'name', name: 'Internal name', type: 'single_line_text_field', required: true},
      {key: 'content', name: 'Message', type: 'rich_text_field', required: true},
    ]}});
    throwUserErrors(created.data.metaobjectDefinitionCreate);
    definition = created.data.metaobjectDefinitionCreate.metaobjectDefinition;
  }
  if (definition.access.storefront !== 'PUBLIC_READ' ||
      !['name:single_line_text_field', 'content:rich_text_field'].every((expected) => definition.fieldDefinitions.some((field: any) => `${field.key}:${field.type.name}` === expected))) {
    throw new Error('The Disclaimer definition must have Internal name (single-line text), Message (rich text), and storefront public-read access.');
  }
  const desiredNames: Record<string, string> = {name: 'Internal name', content: 'Message'};
  const renamedFields = definition.fieldDefinitions.filter((field: any) => desiredNames[field.key] && field.name !== desiredNames[field.key]);
  if (renamedFields.length) {
    const updated = await gqlFetch(`#graphql
      mutation UpdateDisclaimerDefinitionFields($id: ID!, $definition: MetaobjectDefinitionUpdateInput!) {
        metaobjectDefinitionUpdate(id: $id, definition: $definition) {
          metaobjectDefinition { id }
          userErrors { field message code }
        }
      }
    `, {id: definition.id, definition: {fieldDefinitions: renamedFields.map((field: any) => ({update: {key: field.key, name: desiredNames[field.key]}}))}});
    throwUserErrors(updated.data.metaobjectDefinitionUpdate);
  }

}

export async function fetchDisclaimers(ids: string[]) {
  if (!ids.length) return [];
  const result = await gqlFetch(`#graphql
    query ConfiguratorDisclaimers($ids: [ID!]!) { nodes(ids: $ids) { ... on Metaobject { ${disclaimerFields} } } }
  `, {ids});
  return result.data.nodes.map(disclaimerFromMetaobject).filter(Boolean);
}

export async function fetchDisclaimersByHandles(handles: string[]) {
  if (!handles.length) return [];
  if (handles.some((handle) => !isMetaobjectHandle(handle))) throw new Error('Invalid disclaimer handle.');
  const declarations = handles.map((_, index) => '$h' + index + ': MetaobjectHandleInput!').join(', ');
  const selections = handles.map((_, index) => 'n' + index + ': metaobjectByHandle(handle: $h' + index + ') { ' + disclaimerFields + ' }').join('\n');
  const variables = Object.fromEntries(handles.map((handle, index) => ['h' + index, {type: DISCLAIMER_TYPE, handle}]));
  const result = await gqlFetch('query ConfiguratorDisclaimerHandles(' + declarations + ') { ' + selections + ' }', variables);
  return handles.map((_, index) => disclaimerFromMetaobject(result.data['n' + index])).filter(Boolean);
}

export async function fetchDisclaimerCatalog() {
  const disclaimers = [];
  let after = null;
  do {
    const result = await gqlFetch(`#graphql
      query DisclaimerCatalog($after: String) {
        metaobjects(type: "evp_configurator_disclaimer", first: 100, after: $after) {
          nodes { ${disclaimerFields} }
          pageInfo { hasNextPage endCursor }
        }
      }
    `, {after});
    const page = result.data.metaobjects;
    disclaimers.push(...page.nodes.map(disclaimerFromMetaobject).filter(Boolean));
    after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
  } while (after);
  return disclaimers.sort((left, right) => left.name.localeCompare(right.name));
}

export async function saveDisclaimer(value: any, original: any = null) {
  if (!value.name.trim() || !value.text.trim()) throw new Error('Enter a name and content.');
  if (value.name.trim().length > 255 || value.text.length > 50000) throw new Error('The disclaimer name or content is too long.');
  await ensureDisclaimerDefinitions();
  if (original) {
    const [fresh] = await fetchDisclaimers([original.id]);
    if (!fresh || fresh.fingerprint !== original.fingerprint) throw new Error('This disclaimer changed or was removed. Close the dialog and reload before editing.');
  }
  // A name-only edit must not flatten rich text authored in Shopify admin.
  const content = original && value.text === original.text ? original.content : JSON.stringify(plainTextToRichText(value.text));
  const result = await gqlFetch(`#graphql
    mutation SaveConfiguratorDisclaimer($handle: MetaobjectHandleInput!, $metaobject: MetaobjectUpsertInput!) {
      metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
        metaobject { ${disclaimerFields} } userErrors { field message code }
      }
    }
  `, {handle: {type: DISCLAIMER_TYPE, handle: original?.handle || value.handle}, metaobject: {fields: [
    {key: 'name', value: value.name.trim()}, {key: 'content', value: content},
  ]}});
  throwUserErrors(result.data.metaobjectUpsert);
  return disclaimerFromMetaobject(result.data.metaobjectUpsert.metaobject);
}
